import { useState, useContext, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  useToast,
  FormErrorMessage,
  Heading,
  Flex,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGetBranch, apiUpdateBranch, apiCreateBranch } from 'api';

import type { APIBranchData } from 'types/api.types';

import Card from 'components/card/Card';

import { AuthContext } from 'contexts/AuthContext';

import { ROLE_ADMIN } from 'variables/constants';

const BranchFormView = () => {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  // Determine if we're in edit mode
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
  });

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch branch data if in edit mode
  const {
    data: branchData,
    isLoading: isLoadingBranch,
    error: branchError,
  } = useQuery<APIBranchData>({
    queryKey: ['branch', id],
    queryFn: () => apiGetBranch(parseInt(id!)),
    enabled: isEditMode, // Only fetch if we're editing an existing branch
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    if (branchData && isEditMode) {
      setFormData({
        name: branchData.name || '',
        phone: branchData.phone || '',
      });
    }
  }, [branchData, isEditMode]);

  // Create branch mutation
  const createBranchMutation = useMutation({
    mutationFn: (data: any) => apiCreateBranch(data),
    onSuccess: () => {
      toast({
        title: 'Branch created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      navigate('/admin/settings/branches');
    },
    onError: handleApiError,
  });

  // Update branch mutation
  const updateBranchMutation = useMutation({
    mutationFn: ({ branchId, data }: { branchId: number; data: any }) =>
      apiUpdateBranch(branchId, data),
    onSuccess: () => {
      toast({
        title: 'Branch updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      queryClient.invalidateQueries({ queryKey: ['branch', id] });
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      queryClient.invalidateQueries({ queryKey: ['acturis-config-status'] });

      navigate('/admin/settings/branches');
    },
    onError: handleApiError,
  });

  // Handle API errors
  function handleApiError(error: any) {
    toast({
      title: `Error ${isEditMode ? 'updating' : 'creating'} branch`,
      description: 'Please check the form for errors',
      status: 'error',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });

    const formattedErrors: Record<string, string> = {};

    if (typeof error === 'object') {
      Object.entries(error).forEach(([field, messages]) => {
        if (Array.isArray(messages) && messages.length > 0) {
          formattedErrors[field] = messages[0];
        } else if (typeof messages === 'string') {
          formattedErrors[field] = messages;
        }
      });
    }

    setErrors(formattedErrors);
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const branchPayload = {
        name: formData.name,
        phone: formData.phone,
      };

      if (isEditMode && id) {
        updateBranchMutation.mutate({
          branchId: parseInt(id),
          data: branchPayload,
        });
      } else {
        createBranchMutation.mutate(branchPayload);
      }
    } catch (error) {
      toast({
        title: `Error ${isEditMode ? 'updating' : 'creating'} branch`,
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Show loading state if branch data is loading (in edit mode)
  if (isEditMode && isLoadingBranch) {
    return (
      <Card>
        <Flex align="center" justify="center">
          <Spinner size="sm" mr="10px" /> Loading data...
        </Flex>
      </Card>
    );
  }

  // Show error state if branch data failed to load (in edit mode)
  if (isEditMode && branchError) {
    return (
      <Card>
        <Flex align="center" justify="center" color="red.500">
          Error loading branch data. Please try again.
        </Flex>
      </Card>
    );
  }

  const isSubmitting =
    createBranchMutation.isPending || updateBranchMutation.isPending;

  // Check if user has permission to edit/create branches
  const hasPermission = user.role === ROLE_ADMIN;

  if (!hasPermission) {
    return (
      <Card>
        <Flex align="center" justify="center" color="red.500">
          You do not have permission to {isEditMode ? 'edit' : 'create'}{' '}
          branches.
        </Flex>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card mb="20px">
        <Heading size="md" mb={6} color={textColor}>
          {isEditMode ? 'Edit Branch' : 'Create New Branch'}
        </Heading>

        {errors.non_field_errors && (
          <Box mb={6} p={3} borderRadius="md" bg="red.100" color="red.700">
            {errors.non_field_errors}
          </Box>
        )}

        <FormControl isRequired isInvalid={!!errors.name} mb={6}>
          <FormLabel>Branch Name</FormLabel>
          <Input
            variant="main"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter branch name"
          />
          <FormErrorMessage>{errors.name}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.phone} mb={6}>
          <FormLabel>Branch Phone</FormLabel>
          <Input
            variant="main"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="e.g. 905-346-2190"
          />
          <FormErrorMessage>{errors.phone}</FormErrorMessage>
        </FormControl>

        <Flex mt={10} justify="flex-end">
          <Button
            variant="outline"
            mr={3}
            onClick={() => navigate('/admin/settings/branches')}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            type="submit"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Updating' : 'Creating'}
          >
            {isEditMode ? 'Update Branch' : 'Create Branch'}
          </Button>
        </Flex>
      </Card>
    </form>
  );
};

export default BranchFormView;
